import { Controller } from "@hotwired/stimulus"

// A horizontal scrollbar for wide tables that stays pinned to the bottom of the viewport.
//
// Two problems with the native one, both measured on a real 87-column spreadsheet import
// (~7,800px of table inside a ~700px column):
//
//   1. macOS uses overlay scrollbars that occupy zero layout height until you scroll — the
//      container reports a 0px scrollbar track — so there is no visible affordance at all.
//      Styling ::-webkit-scrollbar does not bring it back, and setting the standard
//      `scrollbar-width` actively suppresses those rules in Chrome. Hence a custom thumb.
//   2. Even when visible, the native scrollbar sits at the BOTTOM OF THE TABLE. On a 131-row
//      sheet that is far below the fold, so reading the top of a wide table means scrolling
//      to the bottom to find the control, dragging, then scrolling back up.
//
// The rail is `position: sticky; bottom: 0` inside a per-table wrapper, so it rides the bottom
// of the window while any part of its table is on screen and releases once you scroll past.
// Wrapping happens here rather than in MarkdownRenderer so the stored Markdown and its
// server-rendered HTML stay unchanged — this is purely a progressive enhancement, and the
// table scrolls with a trackpad exactly as before if the JS never runs.
export default class extends Controller {
  connect() {
    this.bars = []
    this.mount()

    // A Turbo morph does NOT re-run connect(): the layout sets turbo_refreshes_with
    // method: :morph, and morph mutates the DOM in place, so .fd-prose keeps its attributes
    // and Stimulus fires neither disconnect() nor connect(). Meanwhile idiomorph soft-matches
    // our wrapper against the server's .fd-table-scroll (neither has an id), rewrites its
    // class, and drops the rail with it. Any same-URL redirect renders this way — posting a
    // comment on this very page is enough — so without this the rail silently disappears for
    // the rest of the session and never comes back.
    this.onMorph = () => this.mount()
    document.addEventListener("turbo:morph", this.onMorph)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.onMorph)
    this.observer?.disconnect()
    this.mountToken = null
    this.unwrapAll()
    this.bars = []
  }

  // Build (or rebuild) a rail for every table, starting from whatever is actually in the DOM.
  mount() {
    this.observer?.disconnect()
    this.bars = []
    this.unwrapAll()

    this.element.querySelectorAll(".fd-table-scroll").forEach((box) => this.attach(box))
    if (this.bars.length === 0) return

    // Whether a table overflows is not settled here: the container resizes with the window,
    // and webfonts swap in afterwards and can widen a table past its container. So every rail
    // is built up front and sync() decides whether to show it, rather than measuring once and
    // permanently deciding there is nothing to scroll. Measuring in one pass after all the
    // DOM writes also keeps this to a single layout rather than one per table.
    this.syncAll()
    this.observer = new ResizeObserver(() => this.syncAll())
    this.bars.forEach(({ box }) => this.observer.observe(box))

    // fonts.ready can resolve after a disconnect or a later remount; only the current mount
    // may act on it, or it re-syncs bars that point at detached nodes.
    const token = (this.mountToken = {})
    document.fonts?.ready.then(() => { if (this.mountToken === token) this.syncAll() })
  }

  // Strip our wrappers from the LIVE DOM rather than from this.bars. After a morph those
  // recorded nodes are unreliable: idiomorph recycles the wrapper into the document and
  // detaches the original box, so replacing wrapper with box would swap live content for a
  // stale copy. Querying the document instead is correct in every case.
  unwrapAll() {
    this.element.querySelectorAll(".fd-table-block").forEach((block) => {
      const box = block.querySelector(":scope > .fd-table-scroll")
      if (box) block.replaceWith(box); else block.remove()
    })
    this.element.querySelectorAll(".fd-table-rail").forEach((rail) => rail.remove())
  }

  syncAll() {
    this.bars.forEach((bar) => this.sync(bar))
  }

  attach(box) {
    const wrapper = document.createElement("div")
    wrapper.className = "fd-table-block"
    box.replaceWith(wrapper)
    wrapper.appendChild(box)

    const rail = document.createElement("div")
    rail.className = "fd-table-rail"
    rail.innerHTML = '<div class="fd-table-rail-thumb"></div>'
    rail.setAttribute("aria-hidden", "true") // the table itself is already keyboard-scrollable
    wrapper.appendChild(rail)

    const bar = { box, wrapper, rail, thumb: rail.firstElementChild }
    this.bars.push(bar)

    box.addEventListener("scroll", () => this.sync(bar), { passive: true })
    rail.addEventListener("pointerdown", (event) => this.startDrag(event, bar))
  }

  // Thumb width is the visible fraction of the table; its offset is the scroll fraction.
  sync(bar) {
    const { box, rail, thumb } = bar
    const scrollable = box.scrollWidth - box.clientWidth
    if (scrollable <= 0) {
      rail.hidden = true
      return
    }

    rail.hidden = false
    const width = Math.max(rail.clientWidth * (box.clientWidth / box.scrollWidth), 40)
    thumb.style.width = `${width}px`
    thumb.style.transform = `translateX(${(box.scrollLeft / scrollable) * (rail.clientWidth - width)}px)`
  }

  startDrag(event, bar) {
    // Right- and middle-click must not jump the scroll position, the way no real scrollbar
    // does — moveTo() below writes scrollLeft unconditionally.
    if (event.button !== 0 || !event.isPrimary) return

    const { box, rail, thumb } = bar
    const thumbBox = thumb.getBoundingClientRect()
    // Grabbing the thumb keeps the grab point under the pointer; clicking the bare track
    // centres the thumb where you clicked, which reads as "jump here".
    const onThumb = event.clientX >= thumbBox.left && event.clientX <= thumbBox.right
    const grabOffset = onThumb ? event.clientX - thumbBox.left : thumbBox.width / 2

    // Measured once: none of this geometry can change during the drag, and re-reading it per
    // pointermove forces a layout against the style writes sync() just made.
    const railLeft = rail.getBoundingClientRect().left
    const travel = rail.clientWidth - thumb.clientWidth
    const scrollable = box.scrollWidth - box.clientWidth

    const moveTo = (clientX) => {
      if (travel <= 0) return
      const position = Math.min(Math.max(clientX - railLeft - grabOffset, 0), travel)
      box.scrollLeft = (position / travel) * scrollable
    }

    moveTo(event.clientX)
    // Capture keeps the drag alive when the pointer leaves the rail vertically, which happens
    // constantly on a 12px target.
    rail.setPointerCapture(event.pointerId)
    rail.classList.add("is-dragging")
    event.preventDefault()

    const onMove = (moveEvent) => moveTo(moveEvent.clientX)
    const onUp = () => {
      rail.classList.remove("is-dragging")
      rail.removeEventListener("pointermove", onMove)
      rail.removeEventListener("pointerup", onUp)
      rail.removeEventListener("pointercancel", onUp)
    }

    rail.addEventListener("pointermove", onMove)
    rail.addEventListener("pointerup", onUp)
    rail.addEventListener("pointercancel", onUp)
  }
}
